<template>
<div :class="$style.block">
  <div :class="$style.background"></div>

  <div :class="$style.container">
    <div :class="$style.slidesContainer">
      <span class="line-title">Team</span>
      <ul :class="$style.list" :style="{ height }">
        <li
          :class="$style.content"
          data-slide
          v-for="slide in content.slides"
          :key="slide.src"
          ref="text">
            <h4 data-text v-html="slide.title"></h4>
            <p  data-text v-html="slide.subtitle"></p>
            <p  data-text :class="$style.text" v-html="slide.text"></p>
          </li>
      </ul>
    </div>
    <div :class="$style.picture">
      <ul>
        <li
          v-for="slide in content.slides"
          :key="slide.src"
          ref="image"
          :style="{ backgroundImage: `url(${slide.src})` }"></li>
      </ul>
      <div :class="$style.arrowContainer">
        <div :class="[$style.arrow, $style.arrowRight]">
          <ArrowButton :action="next" :direction="'right'"></ArrowButton>
        </div>
        <div :class="[$style.arrow, $style.arrowLeft]">
          <ArrowButton :action="prev" :direction="'left'"></ArrowButton>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import gsap from 'gsap'

import ArrowButton from '@/components/ArrowButton'
import TextBlock from '@/components/TextBlock'

export default {
  props: ['content'],
  data() {
    return {
      currentIndex: 0,
      prevIndex: -1,
      isAnimating: false,
      direction: 0,
      height: 0
    }
  },

  components: {
    TextBlock,
    ArrowButton
  },

  mounted() {
    const h = Array.from(this.$el.querySelectorAll('[data-slide]')).reduce((previous, current) => {
      const currentBounds = current.getBoundingClientRect()
      return (previous > currentBounds.height) ? previous : currentBounds.height
    })

    this.height = `${h}px`

    gsap.set(this.$refs.image, { autoAlpha: 0 })
    gsap.set(this.$refs.image[this.currentIndex], { autoAlpha: 1 })

    gsap.set(this.$refs.text, { autoAlpha: 0 })
    gsap.set(this.$refs.text[this.currentIndex], { autoAlpha: 1 })
  },

  methods: {
    next() {
      if (this.isAnimating) return

      this.prevIndex = this.currentIndex
      this.currentIndex = this.currentIndex === (this.content.slides.length - 1) ? 0 : this.currentIndex + 1

      const done = () => { this.isAnimating = false }
      const tl = new gsap.TimelineMax({ onComplete: done })
      const current = this.currentIndex
      const prev = this.prevIndex

      const currentImage = this.$refs.image[current]
      const oldImage = this.$refs.image[prev]
      const currentTexts = this.$refs.text[current]
      const oldTexts = this.$refs.text[prev]

      tl
        .set(oldImage, {
          zIndex: 1
        })
        .set(currentImage, {
          zIndex: 5,
          xPercent: -100
        })

        .add('start')
        .to(currentImage, 1.3, {
          xPercent: 0,
          autoAlpha: 1,
          ease: gsap.Expo.easeInOut
        }, 'start')

        .to(oldTexts, 0.8, {
          autoAlpha: 0,
          zIndex: 1
        }, 'start')

        .fromTo(currentTexts, 0.8, {
          autoAlpha: 0,
          xPercent: -18,
          zIndex: 2
        }, {
          autoAlpha: 1,
          xPercent: 0,
          ease: gsap.Expo.easeInOut
        }, 'start')

        .set(oldImage, {
          autoAlpha: 0
        })
    },

    prev() {
      if (this.isAnimating) return

      this.prevIndex = this.currentIndex
      this.currentIndex = this.currentIndex === 0 ? this.content.slides.length - 1 : this.currentIndex - 1

      const done = () => { this.isAnimating = false }
      const tl = new gsap.TimelineMax({ onComplete: done })
      const current = this.currentIndex
      const prev = this.prevIndex

      const currentImage = this.$refs.image[current]
      const oldImage = this.$refs.image[prev]
      const currentTexts = this.$refs.text[current]
      const oldTexts = this.$refs.text[prev]

      tl
        .set(oldImage, {
          zIndex: 1
        })
        .set(currentImage, {
          zIndex: 5,
          xPercent: 100
        })

        .add('start')
        .to(currentImage, 1.3, {
          xPercent: 0,
          autoAlpha: 1,
          ease: gsap.Expo.easeInOut
        }, 'start')

        .to(oldTexts, 0.8, {
          autoAlpha: 0,
          zIndex: 1
        }, 'start')

        .fromTo(currentTexts, 0.8, {
          autoAlpha: 0,
          xPercent: 30,
          zIndex: 2
        }, {
          autoAlpha: 1,
          xPercent: 0,
          ease: gsap.Expo.easeInOut
        }, 'start')

        .set(oldImage, {
          autoAlpha: 0
        })
    }
  }
}
</script>

<style module lang="scss">
@import "~sass-mq/mq";

.block {
  position: relative;
  padding: $big-margin-mobile;

  @include mq(sm) {
    padding: ($big-margin * 2) $big-margin;
  }
}

.container {
  @include grid-size();
  display: flex;
  flex-flow: column;

  @include mq(sm) {
    justify-content: space-between;
    flex-flow: row-reverse;
    text-align: left;
  }
}

.slidesContainer {
  width: 100%;
  padding: $big-margin-mobile;

  @include mq(sm) {
    display: block;
    padding: $big-margin 0;
    padding-left: $big-margin;
  }
}

.background {
  display: none;
  position: absolute;
  top: 0;
  right: 0;
  width: 60%;
  height: 100%;
  background-color: $color-dark-two;
  z-index: -1;

  @include mq(sm) {
    display: block;
  }
}

.list {
  position: relative;
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  margin: $standard-margin-mobile 0;

  @include mq(sm) {
    flex-flow: column;
  }
}

.content {
  position: absolute;
  top: 0;
  left: 0;
  max-width: 350px;

  .text {
    color: gray;

    @include mq(sm) {
      margin-top: $small-margin;
    }
  }
}

.picture {
  position: relative;
  flex: 0 0 auto;
  background: $color-dark;

  @include mq(sm) {
    width: 60%;
  }

  ul {
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    z-index: 0;
    width: 80%;
    height: 80%;
    margin: auto;
    overflow: hidden;

    @include mq(sm) {
      width: 70%;
      height: 70%;
    }
  }

  &:before {
    content: "";
    display: block;
    width: 100%;
    padding-bottom: 90%;
  }

  li {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-size: cover;
    background-position: center;
  }
}

.arrowContainer {
  position: absolute;
  top: 50%;
  right: -30px;
  z-index: 20;
  transform: translateY(-50%);

  @include mq(sm) {
    margin: 0 $standard-margin;
    right: -60px;
  }

  .arrowRight {
    margin-bottom: 20px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Team.vue